import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../../axiosInstance';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Badge from 'apollo-react/components/Badge';
import io from 'socket.io-client';

import StyledTabs from '../../common/StyledTabs/StyledTabs';
import ReviewsListCards from '../../common/Reviews_Cards/list';
import {
  REQUEST_STATUS,
  REVIEWER_STATUS_ORDER,
  REVIEWER_TAG_OPTIONS,
  TAB_STATE_KEYS,
} from '../../../constants/request.constant';
import { useActiveTab } from '../../../contexts/ActiveTabContext';

const Approvals = ({ persona }) => {
  const { t } = useTranslation();
  const { activeTab, setActiveTab } = useActiveTab(
    TAB_STATE_KEYS.ADMIN_APPROVALS
  );
  const [status, setStatus] = useState(
    () => REVIEWER_STATUS_ORDER[activeTab] || REQUEST_STATUS.ASSIGNED
  );
  const [requests, setRequests] = useState([]);
  const [count, setCount] = useState({
    [REQUEST_STATUS.ASSIGNED]: 0,
    [REQUEST_STATUS.PENDING]: 0,
    [REQUEST_STATUS.APPROVED]: 0,
    [REQUEST_STATUS.REJECTED]: 0,
  });
  const navigate = useNavigate();
  const statusRef = useRef(status);
  const socketRef = useRef(null);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    setStatus(REVIEWER_STATUS_ORDER[activeTab] || REQUEST_STATUS.ASSIGNED);
  }, [activeTab]);

  useEffect(() => {
    setRequests([]);

    const fetchRequests = async () => {
      try {
        const response = await axiosInstance.get(
          // eslint-disable-next-line no-undef
          `${process.env.REACT_APP_SERVER_URL}/api/requests`,
          {
            params: { status },
          },
          { withCredentials: true }
        );

        setRequests(response?.data.requests || []);
      } catch (err) {
        console.error('Error fetching requests:', err);
      }
    };

    const fetchRequestsCount = async () => {
      try {
        const response = await axiosInstance.get(
          // eslint-disable-next-line no-undef
          `${process.env.REACT_APP_SERVER_URL}/api/requests/count`
        );
        setCount(response?.data.count);
      } catch (err) {
        console.error('Error fetching requests count:', err);
      }
    };

    fetchRequests();
    fetchRequestsCount();
  }, [status]);

  useEffect(() => {
    axiosInstance
      .get(
        // eslint-disable-next-line no-undef
        `${process.env.REACT_APP_SERVER_URL}/api/users/me`
      )
      .then((response) => {
        const userId = response?.data?.user?.id;

        if (!userId) {
          console.error('No user ID found');
          return;
        }

        // eslint-disable-next-line no-undef
        const socket = io(process.env.REACT_APP_SERVER_URL, {
          withCredentials: true,
          query: {
            userId: userId,
          },
        });

        socketRef.current = socket;

        const onChanged = (data) => {
          if (data?.scope === 'admin') {
            axiosInstance
              // eslint-disable-next-line no-undef
              .get(`${process.env.REACT_APP_SERVER_URL}/api/requests/count`)
              .then((response) => {
                setCount(response?.data.count);
              })
              .catch((error) => {
                console.error('Failed to fetch count:', error);
              });

            axiosInstance
              .get(
                // eslint-disable-next-line no-undef
                `${process.env.REACT_APP_SERVER_URL}/api/requests`,
                { params: { status: statusRef.current } },
                { withCredentials: true }
              )
              .then((response) => {
                setRequests(response?.data.requests || []);
              })
              .catch((error) => {
                console.error('Failed to fetch requests:', error);
              });
          }
        };

        socket.on('requests:changed', onChanged);
      })
      .catch((error) => {
        console.error('Failed to fetch user info:', error);
      });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const handleTabChange = (event, value) => {
    setStatus(REVIEWER_STATUS_ORDER[value] || REQUEST_STATUS.ASSIGNED);
    setActiveTab(value);
  };

  const tabConfigs = [
    {
      key: 'assigned',
      label: (
        <>
          {t('Assigned')}
          <Badge badgeContent={count[REQUEST_STATUS.ASSIGNED]} />
        </>
      ),
    },
    {
      key: 'in_progress',
      label: (
        <>
          {t('In Progress')}
          <Badge badgeContent={count[REQUEST_STATUS.PENDING]} />
        </>
      ),
    },
    {
      key: 'approved',
      label: (
        <>
          {t('Approved')}
          <Badge badgeContent={count[REQUEST_STATUS.APPROVED]} />
        </>
      ),
    },
    {
      key: 'rejected',
      label: (
        <>
          {t('Rejected')}
          <Badge badgeContent={count[REQUEST_STATUS.REJECTED]} />
        </>
      ),
    },
  ];

  return (
    <div>
      <StyledTabs
        value={activeTab}
        onChange={handleTabChange}
        tabs={tabConfigs}
      />
      <ReviewsListCards
        requests={requests}
        activeTab={activeTab}
        tagOptions={REVIEWER_TAG_OPTIONS[status]}
        status={status}
        onNavigate={(requestId) =>
          navigate(`/${persona}/review/${requestId}`, {
            state: { isRequestActive: activeTab === 0 },
          })
        }
      />
    </div>
  );
};

export default Approvals;
